import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { Check as CheckIcon, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, startOfDay, isBefore, getDay } from 'date-fns';
import availabilityService, { coerceAvailabilityDateKey } from '../services/availabilityService';
import { Availability } from '../types/availability';
import { useAuth } from '../contexts/AuthContext';
import BusinessService from '../services/businessService';
import { Business } from '../types';
import themeService from '../services/themeService';
import inventoryService from '../services/inventoryService';
import plateService from '../services/plateService';
import dishService from '../services/dishService';
import ChaaviVendorDashboardShell, {
  CHA,
  ChaaviVendorNavKey,
} from '../components/ChaaviVendorDashboardShell';
import chatService from '../services/chatService';
import notificationService from '../services/notificationService';

type ItemRow = { id: string; name: string; type: 'theme' | 'inventory' | 'plate' | 'dish'; tag?: string };

type ItemFilter = 'all' | 'theme' | 'inventory' | 'plate' | 'dish';

function businessEmoji(b: Business): string {
  const c = (b.businessCategory || '').toLowerCase();
  if (c.includes('tent')) return '⛺';
  if (c.includes('cater')) return '🍽️';
  if (c.includes('farm')) return '🏡';
  return '🏪';
}

function classifyDay(
  itemType: 'theme' | 'inventory' | 'plate' | 'dish',
  row?: { isAvailable: boolean; availableQuantity: number }
): 'none' | 'available' | 'partial' | 'blocked' {
  if (!row) return 'none';
  if (!row.isAvailable) return 'blocked';
  if (itemType === 'inventory') {
    if (row.availableQuantity <= 0) return 'blocked';
    if (row.availableQuantity < 50) return 'partial';
    return 'available';
  }
  if (row.availableQuantity <= 0) return 'partial';
  return 'available';
}

function itemCardBadge(itemType: 'theme' | 'inventory' | 'plate' | 'dish', list: Availability[]): 'open' | 'partial' | 'blocked' {
  if (list.length === 0) return 'open';
  let hasBlocked = false;
  let hasPartial = false;
  let hasOpen = false;
  for (const a of list) {
    const c = classifyDay(itemType, { isAvailable: a.isAvailable, availableQuantity: a.availableQuantity });
    if (c === 'blocked') hasBlocked = true;
    else if (c === 'partial') hasPartial = true;
    else if (c === 'available') hasOpen = true;
  }
  if (hasBlocked && !hasOpen && !hasPartial) return 'blocked';
  if (hasPartial || (hasBlocked && hasOpen)) return 'partial';
  return 'open';
}

type DayAvail = { isAvailable: boolean; availableQuantity: number };

function dayAvailEqual(a: DayAvail | undefined, b: DayAvail | undefined): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.isAvailable === b.isAvailable && a.availableQuantity === b.availableQuantity;
}

/** Dates that differ from last server snapshot — these need POST /availability. */
function changedAvailabilityKeys(working: Record<string, DayAvail>, baseline: Record<string, DayAvail>): string[] {
  const keyMap: Record<string, true> = {};
  Object.keys(working).forEach((k) => {
    keyMap[k] = true;
  });
  Object.keys(baseline).forEach((k) => {
    keyMap[k] = true;
  });
  const out: string[] = [];
  Object.keys(keyMap).forEach((k) => {
    if (!dayAvailEqual(working[k], baseline[k])) {
      out.push(k);
    }
  });
  return out;
}

const AvailabilityManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [itemFilter, setItemFilter] = useState<ItemFilter>('all');
  const [selectedItem, setSelectedItem] = useState<ItemRow | null>(null);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [workingByDate, setWorkingByDate] = useState<Record<string, DayAvail>>({});
  /** Last loaded from API; Save compares workingByDate to this so POSTs always run when something actually changed. */
  const [baselineByDate, setBaselineByDate] = useState<Record<string, DayAvail>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [hint, setHint] = useState<string>(
    'Tip: Click dates to select, then either click Save (saves as available) or use Mark available / partial / blocked, then Save.'
  );
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [chaaviNav, setChaaviNav] = useState<ChaaviVendorNavKey>('availability');

  /** Ignores stale GET responses when the vendor switches item/business quickly. */
  const availabilityFetchId = useRef(0);

  const todayStart = startOfDay(new Date());

  const fetchNotificationCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      const count = await notificationService.getNotificationCount(user.phoneNumber);
      setUnreadNotificationCount(count);
    } catch {
      /* ignore */
    }
  }, [user?.phoneNumber]);

  const fetchUnreadChatCount = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      const count = await chatService.getTotalUnreadCount(user.phoneNumber);
      setUnreadChatCount(count);
    } catch {
      /* ignore */
    }
  }, [user?.phoneNumber]);

  useEffect(() => {
    if (!user?.phoneNumber) return;
    fetchNotificationCount();
    fetchUnreadChatCount();
  }, [user?.phoneNumber, fetchNotificationCount, fetchUnreadChatCount]);

  const getTabIndexForBusiness = (label: string, business: Business | null): number => {
    if (!business) return -1;
    const isTent = (business.businessCategory || '').toLowerCase().includes('tent');
    const isCatering = (business.businessCategory || '').toLowerCase().includes('cater');
    if (isTent || isCatering) {
      if (label === 'Notifications') return 4;
      if (label === 'Orders') return 3;
      return 0;
    }
    if (label === 'Orders') return 3;
    if (label === 'Notifications') return 4;
    return 0;
  };

  const handleChaaviNav = (key: ChaaviVendorNavKey) => {
    setChaaviNav(key);
    switch (key) {
      case 'dashboard':
        navigate('/vendor-dashboard');
        break;
      case 'business':
        navigate('/vendor-dashboard');
        window.setTimeout(() => {
          document.getElementById('vendor-business-selector')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200);
        break;
      case 'availability':
        navigate('/availability');
        break;
      case 'messages':
        navigate('/vendor-chat');
        break;
      case 'reviews':
        navigate('/vendor-dashboard');
        break;
      case 'analytics':
        break;
      default:
        break;
    }
  };

  const handleChaaviOpenNotifications = () => {
    if (!selectedBusiness) {
      navigate('/vendor-orders-notifications');
      return;
    }
    const idx = getTabIndexForBusiness('Notifications', selectedBusiness);
    if (idx >= 0) {
      navigate('/vendor-dashboard', { state: { activeTab: idx } });
    } else {
      navigate('/vendor-orders-notifications');
    }
  };

  const fetchBusinesses = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      setLoading(true);
      setError(null);
      const vendorBusinesses = await BusinessService.getBusinessesByVendorPhoneNumber(user.phoneNumber);
      setBusinesses(vendorBusinesses);
      setSelectedBusiness((prev) => prev ?? (vendorBusinesses[0] || null));
    } catch (err: any) {
      setError('Failed to fetch businesses: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [user?.phoneNumber]);

  const fetchItems = useCallback(async () => {
    if (!selectedBusiness) return;

    try {
      setLoading(true);
      const allItems: ItemRow[] = [];

      try {
        const themes = await themeService.getThemesByBusinessId(selectedBusiness.businessId);
        themes.forEach((theme) => {
          allItems.push({
            id: theme.themeId,
            name: theme.themeName,
            type: 'theme',
            tag: theme.themeCategory || 'Theme',
          });
        });
      } catch (err) {
        console.error('Error fetching themes:', err);
      }

      try {
        const inventory = await inventoryService.getInventoryByBusinessId(selectedBusiness.businessId);
        inventory.forEach((inv) => {
          allItems.push({
            id: inv.inventoryId,
            name: `${inv.inventoryName} (×${inv.quantity})`,
            type: 'inventory',
            tag: inv.inventoryCategory || 'Inventory',
          });
        });
      } catch (err) {
        console.error('Error fetching inventory:', err);
      }

      try {
        const plates = await plateService.getPlatesByBusinessId(selectedBusiness.businessId);
        plates.forEach((plate) => {
          allItems.push({
            id: plate.plateId,
            name: plate.dishName,
            type: 'plate',
            tag: plate.dishType === 'veg' ? 'Veg plate' : 'Non-veg plate',
          });
        });
      } catch (err) {
        console.error('Error fetching plates:', err);
      }

      const isCatering = (selectedBusiness.businessCategory || '').toLowerCase().includes('cater');
      if (isCatering) {
        try {
          const dishes = await dishService.getDishesByBusinessId(selectedBusiness.businessId);
          dishes.forEach((dish) => {
            allItems.push({
              id: dish.dishId,
              name: dish.dishName,
              type: 'dish',
              tag: dish.dishType === 'veg' ? 'Veg dish' : 'Non-veg dish',
            });
          });
        } catch (err) {
          console.error('Error fetching dishes:', err);
        }
      }

      setItems(allItems);
      if (allItems.length > 0) {
        setSelectedItem((prev) => {
          if (prev && allItems.some((i) => i.id === prev.id && i.type === prev.type)) return prev;
          return allItems[0];
        });
      } else {
        setSelectedItem(null);
      }
    } catch (err: any) {
      setError('Failed to fetch items: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  const fetchAvailabilities = useCallback(async () => {
    if (!selectedItem) return;

    const fetchId = ++availabilityFetchId.current;
    try {
      setLoading(true);
      setError(null);
      const data = await availabilityService.getAvailabilitiesForItem(selectedItem.id, selectedItem.type);
      if (fetchId !== availabilityFetchId.current) return;
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[AvailabilityManagement] fetched from API', {
          itemId: selectedItem.id,
          itemType: selectedItem.type,
          rows: data.length,
        });
      }
      setAvailabilities(data);
    } catch (err: any) {
      if (fetchId !== availabilityFetchId.current) return;
      setError('Failed to fetch availabilities: ' + (err.message || 'Unknown error'));
    } finally {
      if (fetchId === availabilityFetchId.current) {
        setLoading(false);
      }
    }
  }, [selectedItem]);

  useEffect(() => {
    if (user?.userType === 'VENDOR' && user.phoneNumber) {
      fetchBusinesses();
    }
  }, [user?.userType, user?.phoneNumber, fetchBusinesses]);

  useEffect(() => {
    if (selectedBusiness) {
      fetchItems();
    }
  }, [selectedBusiness, fetchItems]);

  useEffect(() => {
    if (selectedItem && selectedBusiness) {
      fetchAvailabilities();
    }
  }, [selectedItem, selectedBusiness, fetchAvailabilities]);

  useEffect(() => {
    const next: Record<string, { isAvailable: boolean; availableQuantity: number }> = {};
    availabilities.forEach((a) => {
      const key = coerceAvailabilityDateKey(a.availabilityDate);
      if (!key) return;
      next[key] = {
        isAvailable: a.isAvailable,
        availableQuantity: a.availableQuantity,
      };
    });
    setWorkingByDate(next);
    setBaselineByDate({ ...next });
  }, [availabilities]);

  const pendingSaveKeys = useMemo(
    () => changedAvailabilityKeys(workingByDate, baselineByDate),
    [workingByDate, baselineByDate]
  );

  const saveButtonLabel = useMemo(() => {
    if (saving) return 'save-loading';
    if (pendingSaveKeys.length > 0) return `Save changes (${pendingSaveKeys.length})`;
    if (selectedDates.size > 0) return `Save changes (${selectedDates.size} selected)`;
    return 'Save changes';
  }, [saving, pendingSaveKeys.length, selectedDates.size]);

  const filteredItems = useMemo(() => {
    if (itemFilter === 'all') return items;
    return items.filter((i) => i.type === itemFilter);
  }, [items, itemFilter]);

  const presetFor = useCallback(
    (mode: 'available' | 'partial' | 'blocked'): { isAvailable: boolean; availableQuantity: number } => {
      if (!selectedItem) return { isAvailable: true, availableQuantity: 1 };
      if (mode === 'blocked') return { isAvailable: false, availableQuantity: 0 };
      if (mode === 'partial') {
        if (selectedItem.type === 'inventory') return { isAvailable: true, availableQuantity: 25 };
        return { isAvailable: true, availableQuantity: 1 };
      }
      if (selectedItem.type === 'inventory') return { isAvailable: true, availableQuantity: 100 };
      return { isAvailable: true, availableQuantity: 1 };
    },
    [selectedItem]
  );

  const rowForDate = (dateStr: string) => workingByDate[dateStr];

  const toggleDateKey = (dateStr: string) => {
    setSelectedDates((prev) => {
      const n = new Set(prev);
      if (n.has(dateStr)) n.delete(dateStr);
      else n.add(dateStr);
      return n;
    });
  };

  const applyPresetToSelection = (mode: 'available' | 'partial' | 'blocked') => {
    if (selectedDates.size === 0) {
      setHint('Select one or more dates on the calendar first.');
      return;
    }
    const p = presetFor(mode);
    setWorkingByDate((prev) => {
      const n = { ...prev };
      selectedDates.forEach((d) => {
        n[d] = { ...p };
      });
      return n;
    });
    setSelectedDates(new Set());
    setHint('Done! Changes applied in the calendar. Click Save changes to persist to the server.');
  };

  const clearSelection = () => {
    setSelectedDates(new Set());
    setHint(
      'Tip: Click dates to select, then either click Save (saves as available) or use Mark available / partial / blocked, then Save.'
    );
  };

  const saveChanges = async () => {
    if (!selectedItem || !selectedBusiness) {
      setError('Select a business and item first.');
      return;
    }

    let working: Record<string, DayAvail> = { ...workingByDate };
    let keys = changedAvailabilityKeys(working, baselineByDate);

    // If user picked dates but skipped "Mark …", treat Save as "mark selected as available" then persist.
    if (keys.length === 0 && selectedDates.size > 0) {
      const p = presetFor('available');
      selectedDates.forEach((d) => {
        working[d] = { ...p };
      });
      setWorkingByDate(working);
      setSelectedDates(new Set());
      keys = changedAvailabilityKeys(working, baselineByDate);
    }

    if (keys.length === 0) {
      setHint('No changes to save. Select dates and click Save (saves as available) or use Mark available / partial / blocked first.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[AvailabilityManagement] saving availability rows', {
          itemId: selectedItem.id,
          itemType: selectedItem.type,
          businessId: selectedBusiness.businessId,
          dateCount: keys.length,
        });
      }
      let savedRows = 0;
      for (const rawDate of keys) {
        const availabilityDate = coerceAvailabilityDateKey(rawDate);
        if (!availabilityDate) continue;
        const v = working[rawDate];
        if (!v) continue;
        await availabilityService.createOrUpdateAvailability({
          itemId: String(selectedItem.id),
          itemType: selectedItem.type,
          businessId: String(selectedBusiness.businessId),
          availabilityDate,
          availableQuantity: v.availableQuantity,
          isAvailable: v.isAvailable,
        });
        savedRows += 1;
      }
      if (savedRows === 0 && keys.length > 0) {
        setError('Could not save: no valid calendar dates. Try reloading the page.');
        return;
      }
      await fetchAvailabilities();
      setHint('Saved! Availability updated for this item.');
    } catch (err: any) {
      setError(err.message || 'Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const monthDaysInGrid = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days: (Date | null)[] = [];
    const firstDow = getDay(start);
    for (let i = 0; i < firstDow; i++) days.push(null);
    let d = start;
    while (d <= end) {
      days.push(d);
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    return days;
  }, [currentMonth]);

  const shiftMonth = (delta: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
    setSelectedDates(new Set());
  };
  if (!user || user.userType !== 'VENDOR') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">This page is only accessible to vendors.</Alert>
      </Box>
    );
  }

  const panelHeadSx = {
    px: 2.25,
    py: 1.75,
    borderBottom: '0.5px solid #f0ebe0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    flexWrap: 'wrap' as const,
  };

  const filterButton = (key: ItemFilter, label: string) => (
    <Button
      type="button"
      disableRipple
      onClick={() => setItemFilter(key)}
      sx={{
        py: 0.65,
        px: 1.5,
        borderRadius: '16px',
        border: '0.5px solid',
        borderColor: itemFilter === key ? CHA.accent : '#d6cfc0',
        bgcolor: itemFilter === key ? CHA.accent : 'transparent',
        color: itemFilter === key ? '#fff' : '#7a6a55',
        fontSize: 12,
        textTransform: 'none',
        minWidth: 0,
        fontFamily: 'sans-serif',
        '&:hover': {
          bgcolor: itemFilter === key ? CHA.accentHover : '#f5f0e8',
          borderColor: itemFilter === key ? CHA.accentHover : '#d6cfc0',
        },
      }}
    >
      {label}
    </Button>
  );

  const availBadgeSx = (kind: 'open' | 'partial' | 'blocked') => {
    const map = {
      open: { bg: '#e8f4ed', color: '#3a7a50' },
      partial: { bg: '#fef4e0', color: '#a07020' },
      blocked: { bg: '#fdecea', color: '#a03020' },
    };
    const t = map[kind];
    return {
      fontSize: 10,
      mt: 0.625,
      px: 0.9,
      py: 0.25,
      borderRadius: '6px',
      display: 'inline-block' as const,
      fontFamily: 'sans-serif',
      fontWeight: 500,
      bgcolor: t.bg,
      color: t.color,
    };
  };

  return (
    <ChaaviVendorDashboardShell
      userFirstName={user?.firstName || 'Vendor'}
      footerLabel={
        selectedBusiness
          ? String(selectedBusiness.businessCategory || 'Vendor').replace(/_/g, ' ')
          : 'Vendor'
      }
      unreadChatCount={unreadChatCount}
      unreadNotificationCount={unreadNotificationCount}
      onOpenNotifications={handleChaaviOpenNotifications}
      onAddBusiness={() => navigate('/vendor-dashboard')}
      activeNav={chaaviNav}
      onNav={handleChaaviNav}
      pageTitle="Availability management"
      pageSubtitle="Set date-wise availability for your themes, inventory, and items."
      showAddBusiness={false}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && businesses.length === 0 ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1.35fr)' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, overflow: 'hidden' }}>
              <Box sx={panelHeadSx}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                  Select business
                </Typography>
              </Box>
              <Box sx={{ px: 1.75, py: 1.5 }}>
                {businesses.map((b) => {
                  const active = selectedBusiness?.businessId === b.businessId;
                  return (
                    <Box
                      key={b.businessId}
                      onClick={() => setSelectedBusiness(b)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        py: 1.25,
                        px: 1.75,
                        borderRadius: '9px',
                        border: '0.5px solid',
                        borderColor: active ? CHA.accent : CHA.border,
                        bgcolor: active ? '#fdf0e0' : '#fdfaf5',
                        cursor: 'pointer',
                        mb: 1,
                        transition: 'all 0.12s',
                        '&:hover': { borderColor: active ? CHA.accent : '#c0b090' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: active ? '#fde8c8' : '#f0ebe0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 17,
                          flexShrink: 0,
                        }}
                      >
                        {businessEmoji(b)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                          {b.businessName}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif', mt: 0.125 }}>
                          {(b.businessCategory || 'Business').replace(/_/g, ' ')}
                          {active ? ` · ${items.length} item${items.length === 1 ? '' : 's'}` : ''}
                        </Typography>
                      </Box>
                      {active && (
                        <Box
                          sx={{
                            ml: 'auto',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            bgcolor: CHA.accent,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <Box
                  onClick={() => navigate('/vendor-dashboard')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1.25,
                    px: 1.75,
                    borderRadius: '9px',
                    border: `0.5px solid ${CHA.border}`,
                    bgcolor: '#fdfaf5',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                    '&:hover': { borderColor: '#c0b090' },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '8px',
                      bgcolor: '#f0ebe0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 17,
                    }}
                  >
                    ➕
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                      Add another business
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: CHA.muted, fontFamily: 'sans-serif', mt: 0.125 }}>
                      Manage multiple listings
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, overflow: 'hidden' }}>
              <Box sx={{ ...panelHeadSx, flexDirection: 'column', alignItems: 'stretch' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                    Select item
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                  {filterButton('all', 'All')}
                  {filterButton('theme', 'Themes')}
                  {filterButton('inventory', 'Inventory')}
                  {filterButton('plate', 'Plates')}
                  {selectedBusiness && (selectedBusiness.businessCategory || '').toLowerCase().includes('cater') && filterButton('dish', 'Dishes')}
                </Box>
              </Box>
              <Box sx={{ px: 1.75, py: 1.5 }}>
                {filteredItems.length === 0 ? (
                  <Typography sx={{ color: CHA.muted, fontSize: 13, fontFamily: 'sans-serif' }}>
                    No items for this filter. Add themes, inventory, or plates from your dashboard.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 1,
                    }}
                  >
                    {filteredItems.map((item) => {
                      const sel = selectedItem?.id === item.id && selectedItem?.type === item.type;
                      const badge = itemCardBadge(item.type, sel ? availabilities : []);
                      const badgeLabel = badge === 'open' ? 'Open' : badge === 'partial' ? 'Partial' : 'Blocked';
                      return (
                        <Box
                          key={`${item.id}-${item.type}`}
                          onClick={() => setSelectedItem(item)}
                          sx={{
                            p: '10px 12px',
                            borderRadius: '9px',
                            border: '0.5px solid',
                            borderColor: sel ? CHA.accent : CHA.border,
                            bgcolor: sel ? '#fdf0e0' : '#fdfaf5',
                            cursor: 'pointer',
                            transition: 'all 0.12s',
                            '&:hover': { borderColor: sel ? CHA.accent : '#c0b090' },
                          }}
                        >
                          <Typography sx={{ fontSize: 12, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: CHA.muted, fontFamily: 'sans-serif', mt: 0.25 }}>
                            {item.type === 'theme'
                              ? 'Theme'
                              : item.type === 'inventory'
                                ? 'Inventory'
                                : item.type === 'plate'
                                  ? 'Plate'
                                  : 'Dish'}{' '}
                            · {item.tag || '—'}
                          </Typography>
                          {sel && <Box sx={availBadgeSx(badge)}>{badgeLabel}</Box>}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: `0.5px solid ${CHA.border}`, overflow: 'hidden' }}>
            <Box sx={panelHeadSx}>
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: CHA.ink, fontFamily: 'sans-serif' }}>
                {selectedItem ? `${format(currentMonth, 'MMMM yyyy')} — ${selectedItem.name}` : 'Calendar'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  type="button"
                  disableRipple
                  onClick={() => shiftMonth(-1)}
                  sx={{
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    p: 0,
                    borderRadius: '6px',
                    border: '0.5px solid #d6cfc0',
                    color: '#7a6a55',
                    '&:hover': { bgcolor: '#f5f0e8' },
                  }}
                >
                  <ChevronLeft sx={{ fontSize: 18 }} />
                </Button>
                <Button
                  type="button"
                  disableRipple
                  onClick={() => shiftMonth(1)}
                  sx={{
                    minWidth: 28,
                    width: 28,
                    height: 28,
                    p: 0,
                    borderRadius: '6px',
                    border: '0.5px solid #d6cfc0',
                    color: '#7a6a55',
                    '&:hover': { bgcolor: '#f5f0e8' },
                  }}
                >
                  <ChevronRight sx={{ fontSize: 18 }} />
                </Button>
              </Box>
            </Box>
            <Box sx={{ px: 2.25, py: 2 }}>
              {!selectedItem ? (
                <Typography sx={{ color: CHA.muted, fontSize: 13 }}>Select an item to edit availability.</Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.75 }}>
                    {[
                      { k: 'open', dot: '#e8f4ed', border: '#4a9e6a', label: 'Available' },
                      { k: 'partial', dot: '#fef4e0', border: '#c08020', label: 'Partial' },
                      { k: 'blocked', dot: '#fdecea', border: '#c05040', label: 'Blocked' },
                      { k: 'today', dot: '#fff', border: CHA.accent, label: 'Today' },
                    ].map((leg) => (
                      <Box key={leg.k} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '3px',
                            bgcolor: leg.dot,
                            border: `0.5px solid ${leg.border}`,
                          }}
                        />
                        <Typography sx={{ fontSize: 11, color: '#7a6a55', fontFamily: 'sans-serif' }}>{leg.label}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, 1fr)',
                      gap: '3px',
                      mb: 1.75,
                    }}
                  >
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                      <Typography
                        key={d}
                        sx={{
                          textAlign: 'center',
                          fontSize: 10,
                          color: CHA.muted,
                          fontFamily: 'sans-serif',
                          pb: 0.5,
                          fontWeight: 500,
                        }}
                      >
                        {d}
                      </Typography>
                    ))}
                    {monthDaysInGrid.map((day, idx) => {
                      if (day === null) {
                        return <Box key={`e-${idx}`} sx={{ height: 34, borderRadius: '7px' }} />;
                      }
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const past = isBefore(day, todayStart);
                      const row = rowForDate(dateStr);
                      const vis = selectedItem ? classifyDay(selectedItem.type, row) : 'none';
                      const isToday = isSameCalendarDay(day, todayStart);
                      const isSel = selectedDates.has(dateStr);

                      let bg = 'transparent';
                      let color = CHA.ink;
                      let border = '0.5px solid transparent';
                      if (!past && !isSel) {
                        if (vis === 'available') {
                          bg = '#e8f4ed';
                          color = '#2a6a40';
                        } else if (vis === 'blocked') {
                          bg = '#fdecea';
                          color = '#903020';
                        } else if (vis === 'partial') {
                          bg = '#fef4e0';
                          color = '#906020';
                        }
                      }
                      if (isSel) {
                        bg = CHA.accent;
                        color = '#fff';
                        border = `0.5px solid ${CHA.accent}`;
                      } else if (isToday) {
                        border = `0.5px solid ${CHA.accent}`;
                        if (!isSel && bg === 'transparent') color = CHA.accent;
                      }

                      return (
                        <Box
                          key={dateStr}
                          onClick={() => {
                            if (past) return;
                            toggleDateKey(dateStr);
                          }}
                          sx={{
                            height: 34,
                            borderRadius: '7px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontFamily: 'sans-serif',
                            cursor: past ? 'default' : 'pointer',
                            border,
                            bgcolor: bg,
                            color,
                            fontWeight: isToday ? 500 : 400,
                            opacity: past ? 0.35 : 1,
                            transition: 'all 0.12s',
                            '&:hover':
                              past || isSel
                                ? {}
                                : {
                                    bgcolor: vis === 'none' ? '#fdf0e0' : bg,
                                    borderColor: '#e8c890',
                                  },
                          }}
                        >
                          {format(day, 'd')}
                        </Box>
                      );
                    })}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      fullWidth
                      onClick={() => applyPresetToSelection('available')}
                      sx={{
                        flex: 1,
                        minWidth: 120,
                        py: 1.1,
                        borderRadius: '8px',
                        border: `0.5px solid ${CHA.accent}`,
                        bgcolor: CHA.accent,
                        color: '#fff',
                        fontSize: 12,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        '&:hover': { bgcolor: CHA.accentHover },
                      }}
                    >
                      Mark available
                    </Button>
                    <Button
                      type="button"
                      fullWidth
                      onClick={() => applyPresetToSelection('partial')}
                      sx={{
                        flex: 1,
                        minWidth: 120,
                        py: 1.1,
                        borderRadius: '8px',
                        border: '0.5px solid #d6cfc0',
                        bgcolor: '#fff',
                        color: '#5a4a35',
                        fontSize: 12,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        '&:hover': { bgcolor: '#f5f0e8' },
                      }}
                    >
                      Mark partial
                    </Button>
                    <Button
                      type="button"
                      fullWidth
                      onClick={() => applyPresetToSelection('blocked')}
                      sx={{
                        flex: 1,
                        minWidth: 120,
                        py: 1.1,
                        borderRadius: '8px',
                        border: '0.5px solid #e8c0b8',
                        bgcolor: '#fff',
                        color: '#a03020',
                        fontSize: 12,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        '&:hover': { bgcolor: '#fdecea' },
                      }}
                    >
                      Block dates
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      type="button"
                      onClick={clearSelection}
                      sx={{
                        flex: 1,
                        py: 1.1,
                        borderRadius: '8px',
                        border: '0.5px solid #d6cfc0',
                        bgcolor: '#fff',
                        color: '#5a4a35',
                        fontSize: 12,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        '&:hover': { bgcolor: '#f5f0e8' },
                      }}
                    >
                      Clear selection
                    </Button>
                    <Button
                      type="button"
                      onClick={() => void saveChanges()}
                      disabled={saving || !selectedItem}
                      sx={{
                        flex: 1,
                        py: 1.1,
                        borderRadius: '8px',
                        border: `0.5px solid ${CHA.accent}`,
                        bgcolor: CHA.accent,
                        color: '#fff',
                        fontSize: 12,
                        textTransform: 'none',
                        fontFamily: 'sans-serif',
                        '&:hover': { bgcolor: CHA.accentHover },
                      }}
                    >
                      {saveButtonLabel === 'save-loading' ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        saveButtonLabel
                      )}
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: '#fdfaf5',
                      border: `0.5px solid ${CHA.border}`,
                      borderRadius: '8px',
                      p: '10px 14px',
                      mt: 1.5,
                      fontSize: 12,
                      color: '#7a6a55',
                      fontFamily: 'sans-serif',
                      lineHeight: 1.5,
                    }}
                  >
                    <Typography component="span" sx={{ fontWeight: 500, color: CHA.ink }}>
                      Tip:{' '}
                    </Typography>
                    {hint}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </ChaaviVendorDashboardShell>
  );
};

function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default AvailabilityManagement;
